import { Container, InstanceLifetime } from '@cgs/wge';
import { SlotCheat } from '@cgs/data-access';
import {
  AbstractListener,
  GameStateMachineNotifierComponent,
} from '../../game/components/game_state_machine_notifier_component';
import { Cheat } from '../../game/components/node_tap_action/strategies/cheat_provider_strategy';
import { GameStateMachineStates } from '../../reels_engine/state_machine/game-state-machine-states';
import { T_GameStateMachineNotifierComponent } from '../../type_definitions';
import { CheatManager, CheatManagerToken } from './cheat-manager';
import { CheatUIManager } from './cheat-ui-manager';
import { CheatType } from './types';
import { CheatCommandsToken } from './ui/cheat-commands';
import { CheatConfigsToken } from './ui/cheat-configs';
import { CheatCustomInputToken } from './ui/cheat-custom-input';
import { CheatFastSpinToken } from './ui/cheat-fast-spin';
import { CheatPopup, CheatPopupToken } from './ui/cheat-popup';
import { CheatReelsToken } from './ui/cheat-reels';
import { ILaunchParams, LAUNCH_PARAMS_STORE_TOKEN, LaunchParamsStore } from '../../store';

export const CHEAT_COMPONENT_TOKEN = Symbol('CheatComponent');

export class CheatComponent implements AbstractListener {
  constructor(public container: Container) {
    container
      .resolve<GameStateMachineNotifierComponent>(T_GameStateMachineNotifierComponent)
      .notifier.addListener(this);

    const launchParamsStore = this.container.resolve<LaunchParamsStore>(LAUNCH_PARAMS_STORE_TOKEN);

    if (launchParamsStore.state.cheatEnabled) {
      this.registerComponents(launchParamsStore.state);
      this.initialize();
    } else {
      const unsubscribe = launchParamsStore.stream.on((launchParams) => {
        if (!launchParams.cheatEnabled) {
          return;
        }

        this.registerComponents(launchParams);
        this.initialize();

        unsubscribe();
      });
    }
  }

  protected customConfig(): void {
    //
  }

  protected initialize(): void {
    this.container.register(CheatManagerToken, () => new CheatManager(), {
      lifetime: InstanceLifetime.Singleton,
    });

    new CheatUIManager(this.container);
  }

  protected registerComponents(launchParams: ILaunchParams): void {
    this.container.registerValue(CheatPopupToken, new CheatPopup(this.container));

    this.container.registerValue(CheatCommandsToken, launchParams.cheatCommands);
    this.container.registerValue(CheatConfigsToken, launchParams.cheatConfigs);
    this.container.registerValue(CheatReelsToken, launchParams.cheatReels);
    this.container.registerValue(CheatCustomInputToken, launchParams.customCheatInput);
    this.container.registerValue(CheatFastSpinToken, true);

    this.customConfig();
  }

  public getSpinCheat(): SlotCheat | null {
    const cheatManager = this.container.resolve<CheatManager>(CheatManagerToken, {
      optional: true,
    });

    if (!cheatManager) {
      return null;
    }

    const activeCheat = cheatManager.getActiveCheat();

    const slotCheat = new SlotCheat();

    if (!activeCheat || activeCheat.length === 0) {
      return null;
    }

    activeCheat.forEach((activeCheat) => {
      switch (activeCheat?.type) {
        case CheatType.Config:
          slotCheat.customConfig = activeCheat.value;
          break;
        case CheatType.Command:
          slotCheat.command = activeCheat.value;
          break;
        case CheatType.Reels:
          slotCheat.reelsPositions = activeCheat.value;
          break;
      }
    });

    return slotCheat;
  }

  public getFastSpinMode(): boolean {
    const cheatManager = this.container.resolve<CheatManager>(CheatManagerToken, {
      optional: true,
    });

    if (!cheatManager) {
      return false;
    }

    return cheatManager.getFastSpinMode();
  }

  public doSpin(): void {
    throw new Error('CheatComponent.doSpin is not implemented');
  }

  public onStateEntered(slotState: string): void {
    switch (slotState) {
      case GameStateMachineStates.InitGame:
        this.container
          .resolve<CheatPopup>(CheatPopupToken, { optional: true })
          ?.addSpinLongPressedListener();
        break;
    }
  }

  public dispose(): void {}

  public setCheatType(_: Cheat): void {}
}
